import { type FC, useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import { useAppStore } from '@/stores/app-store';
import { classifyThreat } from '@/algorithms/threat-classifier';
import type { ThreatLevel, AcledEventType } from '@/types';

// ---------------------------------------------------------------------------
// Event Timeline Panel — chronological feed of all event types
// Sources: ACLED, earthquakes, wildfires, news, missiles, drone attacks
// ---------------------------------------------------------------------------

type EventCategory = 'all' | 'conflict' | 'natural' | 'military' | 'news';

interface TimelineEvent {
  id: string;
  time: Date;
  category: EventCategory;
  title: string;
  subtitle?: string;
  severity: ThreatLevel;
  location?: string;
  source: string;
  /** 뉴스 원문 링크 — 있으면 클릭 시 새 탭으로 열림 */
  url?: string;
}

const SEV_COLORS: Record<ThreatLevel, string> = {
  critical: 'text-red-400 border-red-900/60 bg-red-950/30',
  high:     'text-orange-400 border-orange-900/60 bg-orange-950/20',
  medium:   'text-yellow-400 border-yellow-900/60 bg-yellow-950/20',
  low:      'text-emerald-400 border-emerald-900/60 bg-emerald-950/20',
  info:     'text-slate-400 border-slate-800/60 bg-slate-900/20',
};

const SEV_DOT: Record<ThreatLevel, string> = {
  critical: 'bg-red-500',
  high:     'bg-orange-500',
  medium:   'bg-yellow-500',
  low:      'bg-emerald-500',
  info:     'bg-slate-500',
};

const CAT_ICONS: Record<EventCategory, string> = {
  all:      '🌐',
  conflict: '⚔',
  natural:  '🌋',
  military: '✈',
  news:     '📰',
};

// 한국어 intel 관련성 키워드 — classifyThreat(영문 전용)의 보조.
// 이 키워드도 classifyThreat 매칭도 없으면 타임라인에서 제외 (대학소식/연예 등 노이즈 차단)
const KO_INTEL_RE = /미사일|북한|핵|군사|전쟁|공습|드론|무인기|제재|도발|파병|안보|국방|훈련|포격|정상회담|외교|쿠데타|테러|지진|해일|분쟁|휴전|병력|남북|영공|영해|격추|침공|사이버/;

const ACLED_SEV: Record<string, ThreatLevel> = {
  'Battles': 'high',
  'Explosions/Remote violence': 'high',
  'Violence against civilians': 'high',
  'Riots': 'medium',
  'Protests': 'low',
  'Strategic developments': 'info',
};

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3600_000);
  const diffDay = Math.floor(diffMs / 86400_000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  return `${diffDay}d ago`;
}

export const EventTimelinePanel: FC = () => {
  const [category, setCategory] = useState<EventCategory>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // 1분마다 강제 리렌더 — 상대시간("3m ago") 갱신 보장
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const acledEvents    = useAppStore((s) => s.acledEvents);
  const earthquakes    = useAppStore((s) => s.earthquakes);
  const wildfires      = useAppStore((s) => s.wildfires);
  const news           = useAppStore((s) => s.news);

  const events = useMemo<TimelineEvent[]>(() => {
    const all: TimelineEvent[] = [];

    // ACLED conflict events
    for (const e of acledEvents) {
      all.push({
        id: `acled-${e.id}`,
        time: new Date(e.event_date),
        category: 'conflict',
        title: e.sub_event_type,
        subtitle: e.fatalities > 0 ? `${e.fatalities} fatalities — ${e.actor1}${e.actor2 ? ` vs ${e.actor2}` : ''}` : `${e.actor1}`,
        severity: e.severity,
        location: `${e.location}, ${e.country}`,
        source: 'ACLED',
      });
    }

    // Earthquakes
    for (const eq of earthquakes) {
      all.push({
        id: `eq-${eq.id}`,
        time: new Date(eq.timestamp),
        category: 'natural',
        title: `M${eq.magnitude.toFixed(1)} Earthquake`,
        subtitle: eq.place,
        severity: eq.magnitude >= 6.5 ? 'critical' : eq.magnitude >= 5.5 ? 'high' : eq.magnitude >= 4.5 ? 'medium' : 'low',
        source: 'USGS',
      });
    }

    // Wildfires
    for (const wf of wildfires.slice(0, 20)) {
      all.push({
        id: `wf-${wf.id}`,
        time: new Date(wf.timestamp),
        category: 'natural',
        title: 'Active Wildfire',
        subtitle: `FRP: ${wf.frp.toFixed(0)} MW — ${wf.confidence}% confidence`,
        severity: wf.frp > 1000 ? 'high' : wf.frp > 300 ? 'medium' : 'low',
        location: wf.country,
        source: wf.satellite,
      });
    }

    // Top news — intel 관련성 필터 통과분만 (일반 사회/연예/대학 뉴스 제외)
    // defense 피드 출신은 military 카테고리 + 필터 면제 (군사 전문지는 전부 관련 뉴스)
    let newsAdded = 0;
    for (const item of news) {
      if (newsAdded >= 40) break;
      const isDefense = item.feedCategory === 'defense';
      const cls = classifyThreat(item.title);
      const koIntel = KO_INTEL_RE.test(item.title);
      if (!isDefense && cls.level === 'info' && !koIntel) continue;
      newsAdded++;
      all.push({
        id: `news-${item.id}`,
        time: new Date(item.publishedAt),
        category: isDefense ? 'military' : 'news',
        title: item.title.slice(0, 80),
        subtitle: item.source,
        severity: item.severity ?? cls.level,
        source: item.source,
        url: item.url,
      });
    }

    // Sort by time descending
    all.sort((a, b) => b.time.getTime() - a.time.getTime());

    // Filter by category
    const filtered = category === 'all' ? all : all.filter((e) => e.category === category);

    return filtered.slice(0, 80);
  }, [acledEvents, earthquakes, wildfires, news, category]);

  const categories: EventCategory[] = ['all', 'conflict', 'military', 'natural', 'news'];

  return (
    <div className="flex flex-col h-full bg-surface-panel/80 border border-surface-border rounded-lg overflow-hidden" style={{ contain: 'layout' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-surface-border shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Event Timeline</span>
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-950/60 text-red-400">
            {events.length}
          </span>
        </div>
        <span className="text-[9px] text-slate-600">LIVE</span>
      </div>

      {/* Category filter */}
      <div className="flex gap-1 px-2 py-1.5 border-b border-surface-border shrink-0 overflow-x-auto">
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setCategory(cat)}
            className={clsx(
              'shrink-0 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide transition-colors',
              category === cat
                ? 'bg-blue-900/60 text-blue-300 border border-blue-700/50'
                : 'text-slate-500 hover:text-slate-300 hover:bg-surface-hover',
            )}
          >
            {CAT_ICONS[cat]} {cat}
          </button>
        ))}
      </div>

      {/* Timeline list */}
      <div className="flex-1 overflow-y-auto">
        {events.length === 0 && (
          <div className="flex items-center justify-center h-24 text-slate-600 text-[11px]">
            No events
          </div>
        )}
        {events.map((ev) => (
          <button
            key={ev.id}
            onClick={() => {
              // 뉴스 이벤트: 원문 기사를 새 탭으로 열기
              if (ev.url) {
                window.open(ev.url, '_blank', 'noopener,noreferrer');
                return;
              }
              setExpanded(expanded === ev.id ? null : ev.id);
            }}
            className={clsx(
              'w-full text-left px-3 py-2 border-b border-surface-border/50 hover:bg-surface-hover transition-colors',
              SEV_COLORS[ev.severity],
            )}
          >
            <div className="flex items-start gap-2">
              {/* Severity dot */}
              <span className={clsx('w-1.5 h-1.5 rounded-full mt-1.5 shrink-0', SEV_DOT[ev.severity])} />

              <div className="flex-1 min-w-0">
                {/* Title row */}
                <div className="flex items-start justify-between gap-1">
                  <span className="text-[10px] font-semibold text-slate-200 leading-tight truncate">
                    {ev.title}
                    {ev.url && <span className="text-slate-500 ml-1">↗</span>}
                  </span>
                  <span className="text-[8px] text-slate-600 shrink-0 mt-0.5">
                    {formatRelative(ev.time)}
                  </span>
                </div>

                {/* Subtitle */}
                {ev.subtitle && (
                  <div className="text-[9px] text-slate-500 mt-0.5 truncate">
                    {ev.subtitle}
                  </div>
                )}

                {/* Expanded details */}
                {expanded === ev.id && (
                  <div className="mt-1.5 pt-1.5 border-t border-surface-border/50 space-y-0.5">
                    {ev.location && (
                      <div className="text-[9px] text-slate-400">
                        📍 {ev.location}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="text-[8px] text-slate-600">Source: {ev.source}</span>
                      <span className={clsx('text-[8px] px-1 rounded font-bold',
                        ev.severity === 'critical' ? 'bg-red-950 text-red-400' :
                        ev.severity === 'high' ? 'bg-orange-950 text-orange-400' :
                        ev.severity === 'medium' ? 'bg-yellow-950 text-yellow-400' :
                        'bg-slate-800 text-slate-400',
                      )}>
                        {ev.severity.toUpperCase()}
                      </span>
                    </div>
                    <div className="text-[8px] text-slate-600">
                      {ev.time.toLocaleString()}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
